/**
 * Where the desk's address search looks: the town, the villages around it, and further (#382).
 *
 * Three levels and no more — the dispatcher flips between them on a switch while a caller is
 * talking, and a switch has to be read at a glance. Each is a circle or a drawn boundary,
 * drawn with the zone editor's own map, and each wider level searches the narrower ones too.
 * An undrawn Wider is the "anywhere" position, for the airport in the next city.
 *
 * Its own shapes, not zones. A zone prices rides and orders the offer ladder; redrawing one for
 * a surcharge must not change which streets the desk can find. "Start from a zone" copies a
 * zone's shape once, which is the convenience without the coupling.
 *
 * Below the levels, the passenger app's search (#461): our maps or Google, the firm's own
 * places first either way. Only the passenger app — its map is Google's, and Google's terms
 * allow its results on nothing else, so the desk and the driver app stay on ours whatever is
 * chosen here. The choice saves when it is made, as a choice with no other field beside it.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { resolveMapView, type MapHome } from '@taxi/api-client';

import { mapApi, type MapConfiguration } from '../../map/api';
import {
  Banner,
  Button,
  ConfirmDialog,
  cx,
  Dialog,
  EmptyState,
  Field,
  FieldErrors,
  Flagged,
  PageHeader,
  Section,
  Select,
  SettingRow,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';
import {
  riderSearchApi,
  riderSearchSuppliers,
  searchLevels,
  settingsApi,
  type RiderSearchSettings,
  type RiderSearchSupplier,
  zoneShapes,
  type SearchArea,
  type SearchAreaDraft,
  type SearchLevel,
  type Zone,
  type ZoneShape,
  type ZoneVertex,
} from '../api';
// Renamed: this page's own `describe` says what a level is drawn as.
import { describe as sentenceFor, refusal } from '../errors';
import { openRing, RADIUS_MIN, ringOf, type ZoneOutline } from '../zones/geometry';
import { RadiusControl } from '../zones/RadiusControl';

/** MapLibre is most of a megabyte; loaded when this page draws, as the zone editor's is. */
const ZoneMap = lazy(async () => ({ default: (await import('../zones/ZoneMap')).ZoneMap }));
const ZonesOverview = lazy(async () => ({
  default: (await import('../zones/ZonesOverview')).ZonesOverview,
}));

/** Where the editor opens for a firm that has said nothing at all, as on the zones page. */
const NOWHERE_IN_PARTICULAR = { longitude: 25, latitude: 42, zoom: 5 };

const CITY_ZOOM = 11;

const CONFIRMATION_MS = 4000;

/**
 * A first circle for each level, round the firm's home: a town, the villages a short drive
 * out, and far enough to take in the next city's airport. Something to drag rather than a
 * blank map to click on.
 */
const STARTING_RADIUS: Record<SearchLevel, number> = {
  Town: 5_000,
  Nearby: 15_000,
  Wider: 60_000,
};

/** Each level in its own zone colour, far apart on the wheel so the three never read as one. */
const LEVEL_HUE: Record<SearchLevel, number> = { Town: 0, Nearby: 3, Wider: 6 };

/** Same layout as the zones page: settings on the left, the map in the rest of a wide screen. */
const WIDE_LAYOUT = {
  grid: 'min-[1440px]:grid-cols-[minmax(0,900px)_minmax(480px,1fr)]',
  aside: 'min-[1440px]:sticky min-[1440px]:top-0',
  mapHeight: 'min-[1440px]:h-[calc(100dvh-270px)] min-[1440px]:min-h-[440px]',
} as const;

type Draft = SearchAreaDraft & { level: SearchLevel };

function outlineOfLevel(area: SearchArea, name: string): ZoneOutline {
  return {
    id: area.level,
    displayName: name,
    kind: 'Area',
    isActive: true,
    shape: area.shape,
    hue: LEVEL_HUE[area.level],
    depth: 1,
    ring: ringOf(area),
  };
}

export function AddressSearchPage() {
  const { t } = useTranslation();

  const [areas, setAreas] = useState<SearchArea[] | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [configuration, setConfiguration] = useState<MapConfiguration | null>(null);
  const [home, setHome] = useState<MapHome | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  /** The boundary as it was before each change, for Undo — as the zone editor keeps it. */
  const [history, setHistory] = useState<ZoneVertex[][]>([]);
  const [clearing, setClearing] = useState<SearchLevel | null>(null);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  /** The passenger app's search; null until read, and left null on a refusal. */
  const [riderSearch, setRiderSearch] = useState<RiderSearchSettings | null>(null);
  const [riderSearchError, setRiderSearchError] = useState<string | null>(null);
  const [riderSearchSaved, setRiderSearchSaved] = useState(false);
  const [riderSearchSaving, setRiderSearchSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setAreas(await settingsApi.searchAreas());
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();

    void (async () => {
      try {
        setRiderSearch(await riderSearchApi.get());
      } catch (failure) {
        setRiderSearchError(sentenceFor(failure, t, 'settings.couldNotLoad'));
      }
    })();

    void (async () => {
      // The zones are only something to start from and to draw beside; a page that cannot
      // read them still draws.
      setZones(await settingsApi.zones().catch(() => []));
    })();

    void (async () => {
      try {
        const [style, view] = await Promise.all([
          mapApi.configuration(),
          mapApi.home().catch(() => null),
        ]);

        setHome(view);
        setConfiguration(style);
      } catch {
        setConfiguration(null);
      }
    })();
  }, [load, t]);

  useEffect(() => {
    if (!riderSearchSaved) return;

    const timer = setTimeout(() => setRiderSearchSaved(false), CONFIRMATION_MS);

    return () => clearTimeout(timer);
  }, [riderSearchSaved]);

  useEffect(() => {
    if (!saved) return;

    const timer = setTimeout(() => setSaved(false), CONFIRMATION_MS);

    return () => clearTimeout(timer);
  }, [saved]);

  const mapView = resolveMapView(home, configuration, {
    zoom: CITY_ZOOM,
    nowhere: NOWHERE_IN_PARTICULAR,
  });

  const drawn = (level: SearchLevel) => areas?.find((area) => area.level === level) ?? null;

  const levelName = useCallback((level: SearchLevel) => t(`addressSearch.level.${level}`), [t]);

  const outlines = useMemo(
    () => (areas ?? []).map((area) => outlineOfLevel(area, levelName(area.level))),
    [areas, levelName],
  );

  // What the editor draws beside the level being drawn: the other levels, and the firm's
  // zones — so the town can be drawn corner to corner with the city zone it already has.
  const others = useMemo<ZoneOutline[]>(() => {
    const levels = outlines.filter((outline) => outline.id !== draft?.level);
    const zoneOutlines = zones
      .filter((zone) => zone.kind === 'Area')
      .map<ZoneOutline>((zone) => ({
        id: zone.id,
        displayName: zone.displayName,
        kind: zone.kind,
        isActive: zone.isActive,
        shape: zone.shape,
        hue: 7,
        depth: zone.depth,
        ring: ringOf(zone),
      }));

    return [...levels, ...zoneOutlines];
  }, [outlines, zones, draft?.level]);

  function open(level: SearchLevel) {
    const existing = drawn(level);

    setHistory([]);
    setError(null);
    setDraft(
      existing !== null
        ? { ...existing, boundary: existing.boundary === null ? null : openRing(existing.boundary) }
        : {
            level,
            shape: 'Circle',
            centreLongitude: mapView.longitude,
            centreLatitude: mapView.latitude,
            radiusMetres: STARTING_RADIUS[level],
            boundary: null,
          },
    );
  }

  function change<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  }

  function rememberBoundary() {
    if (draft === null || draft.shape !== 'Polygon') return;

    const before = draft.boundary ?? [];

    setHistory((stack) => [...stack, before]);
  }

  function changeBoundary(next: ZoneVertex[]) {
    rememberBoundary();
    change('boundary', next);
  }

  function undoBoundary() {
    const previous = history.at(-1);

    if (previous === undefined) return;

    setHistory((stack) => stack.slice(0, -1));
    change('boundary', previous);
  }

  /** Copies a zone's shape into the draft. Once: the two are not linked afterwards. */
  function copyZone(id: string) {
    const zone = zones.find((candidate) => candidate.id === id);

    if (zone === undefined || draft === null) return;

    setHistory([]);
    setDraft({
      level: draft.level,
      shape: zone.shape,
      centreLongitude: zone.centreLongitude,
      centreLatitude: zone.centreLatitude,
      radiusMetres: zone.radiusMetres,
      boundary: zone.boundary === null ? null : openRing(zone.boundary),
    });
  }

  async function save() {
    if (draft === null) return;

    setSaving(true);
    setError(null);

    try {
      const { level, ...body } = draft;

      await settingsApi.drawSearchArea(level, body);

      // Closed only once the server has taken it, as every dialog here is.
      setDraft(null);
      setSaved(true);
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    } finally {
      setSaving(false);
    }
  }

  async function chooseRiderSearch(supplier: RiderSearchSupplier) {
    setRiderSearchError(null);
    setRiderSearchSaved(false);
    setRiderSearchSaving(true);

    try {
      setRiderSearch(await riderSearchApi.save(supplier));
      setRiderSearchSaved(true);
    } catch (failure) {
      setRiderSearchError(sentenceFor(failure, t, 'settings.couldNotSave'));
    } finally {
      setRiderSearchSaving(false);
    }
  }

  async function clear(level: SearchLevel) {
    setError(null);
    setClearing(null);

    try {
      await settingsApi.clearSearchArea(level);
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    }
  }

  function describe(level: SearchLevel) {
    const area = drawn(level);

    if (area === null) {
      return level === 'Wider' ? t('addressSearch.anywhere') : t('addressSearch.notDrawn');
    }

    return area.shape === 'Circle'
      ? t('zones.circleOf', { radius: area.radiusMetres ?? 0 })
      : t('zones.polygonOf', { corners: (area.boundary?.length ?? 1) - 1 });
  }

  const drawingTools =
    draft !== null && draft.shape === 'Polygon' ? (
      <>
        <Button size="sm" disabled={history.length === 0} onClick={undoBoundary}>
          {t('zones.undo')}
        </Button>
        <Button
          size="sm"
          disabled={(draft.boundary?.length ?? 0) === 0}
          onClick={() => changeBoundary([])}
        >
          {t('zones.clearCorners')}
        </Button>
      </>
    ) : undefined;

  const copyable = zones.filter((zone) => zone.kind === 'Area');

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <div className={cx('grid grid-cols-1 gap-4 items-start', WIDE_LAYOUT.grid)}>
        <div className="flex flex-col gap-4 min-w-0">
          <PageHeader title={t('nav.settings.search')} description={t('nav.settingsAbout.search')} />

          {error !== null && draft === null && <Banner tone="danger">{error.message}</Banner>}
          {saved && <Banner tone="ok">{t('settings.saved')}</Banner>}
          {areas !== null && areas.length === 0 && <Banner tone="warn">{t('addressSearch.nothingDrawn')}</Banner>}

          <Section title={t('addressSearch.levels')} description={t('addressSearch.levelsHelp')}>
            {areas === null ? (
              <EmptyState>{t('app.loading')}</EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('addressSearch.columnLevel')}</Th>
                    <Th>{t('zones.shapeLabel')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {searchLevels.map((level, index) => (
                    <Tr key={level}>
                      <Td wrap>
                        <span className="flex flex-col">
                          <span className="font-medium">
                            {index + 1}. {levelName(level)}
                          </span>
                          <span className="text-secondary text-ink-3">
                            {t(`addressSearch.levelAbout.${level}`)}
                          </span>
                        </span>
                      </Td>
                      <Td wrap>{describe(level)}</Td>
                      <Td>
                        <span className="flex justify-end gap-1.5">
                          <Button size="sm" onClick={() => open(level)}>
                            {drawn(level) === null ? t('addressSearch.draw') : t('surcharges.edit')}
                          </Button>
                          {drawn(level) !== null && (
                            <Button size="sm" variant="danger" onClick={() => setClearing(level)}>
                              {t('addressSearch.clear')}
                            </Button>
                          )}
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

          <Section title={t('addressSearch.rider.title')} description={t('addressSearch.rider.help')}>
            {(riderSearchError !== null || riderSearchSaved) && (
              <div className="px-5 pt-3">
                {riderSearchError !== null ? (
                  <Banner tone="danger">{riderSearchError}</Banner>
                ) : (
                  <Banner tone="ok">{t('settings.saved')}</Banner>
                )}
              </div>
            )}

            {riderSearch === null ? (
              riderSearchError === null && <EmptyState>{t('app.loading')}</EmptyState>
            ) : (
              <SettingRow
                label={t('addressSearch.rider.supplier')}
                help={
                  <>
                    {t(`addressSearch.rider.about.${riderSearch.supplier}`)}
                    {!riderSearch.googleAvailable && (
                      <span className="block mt-1">
                        {riderSearch.supplier === 'Google'
                          ? t('addressSearch.rider.googleFallenBack')
                          : t('addressSearch.rider.googleUnavailable')}
                      </span>
                    )}
                  </>
                }
                last
              >
                <Select
                  id="supplier"
                  value={riderSearch.supplier}
                  disabled={riderSearchSaving}
                  onChange={(e) => void chooseRiderSearch(e.target.value as RiderSearchSupplier)}
                >
                  {riderSearchSuppliers.map((supplier) => (
                    <option
                      key={supplier}
                      value={supplier}
                      disabled={supplier === 'Google' && !riderSearch.googleAvailable}
                    >
                      {t(`addressSearch.rider.suppliers.${supplier}`)}
                    </option>
                  ))}
                </Select>
              </SettingRow>
            )}
          </Section>
        </div>

        <div className={cx('min-w-0', WIDE_LAYOUT.aside)}>
          <Section title={t('addressSearch.overview')} description={t('addressSearch.overviewAbout')}>
            {areas === null || configuration === null ? (
              <EmptyState>{t('app.loading')}</EmptyState>
            ) : (
              <div className="p-3">
                <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
                  <ZonesOverview
                    configuration={configuration}
                    view={mapView}
                    zones={outlines}
                    className={cx('h-110', WIDE_LAYOUT.mapHeight)}
                    help={t('addressSearch.overviewHelp')}
                    labelled={false}
                    onSelect={(id) => {
                      if ((searchLevels as readonly string[]).includes(id)) open(id as SearchLevel);
                    }}
                  />
                </Suspense>
              </div>
            )}
          </Section>
        </div>
      </div>

      <ConfirmDialog
        open={clearing !== null}
        title={t('addressSearch.confirmClearTitle')}
        confirm={t('addressSearch.clear')}
        cancel={t('surcharges.cancelEdit')}
        onConfirm={() => {
          if (clearing !== null) void clear(clearing);
        }}
        onClose={() => setClearing(null)}
      >
        {clearing !== null &&
          t(clearing === 'Wider' ? 'addressSearch.confirmClearWider' : 'addressSearch.confirmClear', {
            level: levelName(clearing),
          })}
      </ConfirmDialog>

      <Dialog
        open={draft !== null}
        wide
        onClose={() => setDraft(null)}
        title={draft === null ? '' : t('addressSearch.editing', { level: levelName(draft.level) })}
        actions={
          <>
            <Button variant="quiet" onClick={() => setDraft(null)}>
              {t('surcharges.cancelEdit')}
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => void save()}>
              {saving ? t('settings.saving') : t('addressSearch.save')}
            </Button>
          </>
        }
      >
        {draft !== null && (
          <div className="flex flex-col gap-3.5">
            {error !== null && <Banner tone="danger">{error.message}</Banner>}

            <p className="m-0 text-secondary text-ink-2">{t(`addressSearch.levelAbout.${draft.level}`)}</p>

            {copyable.length > 0 && (
              <Field id="copyFrom" label={t('addressSearch.copyFrom')} help={t('addressSearch.copyFromHelp')}>
                <Select value="" onChange={(e) => copyZone(e.target.value)}>
                  <option value="">{t('addressSearch.copyChoose')}</option>
                  {copyable.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {'— '.repeat(zone.depth - 1)}
                      {zone.displayName}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field id="shape" label={t('zones.shapeLabel')} help={t('addressSearch.shapeHelp')}>
              <Select
                value={draft.shape}
                onChange={(e) => change('shape', e.target.value as ZoneShape)}
              >
                {zoneShapes.map((shape) => (
                  <option key={shape} value={shape}>
                    {t(`zones.shape.${shape}`)}
                  </option>
                ))}
              </Select>
            </Field>

            {draft.shape === 'Circle' ? (
              <Field id="radiusMetres" label={t('zones.radius')} help={t('addressSearch.radiusHelp')}>
                <RadiusControl
                  value={draft.radiusMetres ?? STARTING_RADIUS[draft.level]}
                  onChange={(metres) => change('radiusMetres', metres)}
                />
              </Field>
            ) : (
              <div className="flex gap-2">{drawingTools}</div>
            )}

            <Flagged id="boundary" also={['centre', 'radiusMetres']}>
              <Suspense fallback={<EmptyState>{t('app.loading')}</EmptyState>}>
                <ZoneMap
                  configuration={configuration}
                  view={mapView}
                  shape={draft.shape}
                  centre={
                    draft.centreLongitude === null || draft.centreLatitude === null
                      ? null
                      : { longitude: draft.centreLongitude, latitude: draft.centreLatitude }
                  }
                  radiusMetres={
                    Number.isFinite(draft.radiusMetres)
                      ? (draft.radiusMetres ?? STARTING_RADIUS[draft.level])
                      : RADIUS_MIN
                  }
                  boundary={draft.boundary ?? []}
                  others={others}
                  tools={
                    draft.shape === 'Circle' ? (
                      <RadiusControl
                        floating
                        value={draft.radiusMetres ?? STARTING_RADIUS[draft.level]}
                        onChange={(metres) => change('radiusMetres', metres)}
                      />
                    ) : (
                      drawingTools
                    )
                  }
                  onCentre={(point: ZoneVertex) =>
                    setDraft((current) =>
                      current === null
                        ? current
                        : { ...current, centreLongitude: point.longitude, centreLatitude: point.latitude },
                    )
                  }
                  onVertex={(point: ZoneVertex, at: number) =>
                    changeBoundary((draft.boundary ?? []).toSpliced(at, 0, point))
                  }
                  onBeginDrag={rememberBoundary}
                  onMoveVertex={(index: number, point: ZoneVertex) =>
                    setDraft((current) =>
                      current === null
                        ? current
                        : {
                            ...current,
                            boundary: (current.boundary ?? []).map((corner, at) =>
                              at === index ? point : corner,
                            ),
                          },
                    )
                  }
                />
              </Suspense>
            </Flagged>
          </div>
        )}
      </Dialog>
    </FieldErrors>
  );
}
